import { Color, Point } from '../PrimitiveTypes'

export type LayerStyle =
  | StyleProperties
  | OverridableStyleProperties
  | GroupLayerStyle

export type GroupLayerStyle = {
  isDirty: boolean
  // In group this is called tint
  fills: Fill[]
  appearance?: Appearance
  shadows: Shadow[]
}

/** All the properties from the "Style" section of Sketch to customize the layer. */
export interface StyleProperties {
  borders?: Border[]
  fills?: Fill[]
  borderOptions?: BorderOptions
  appearance?: Appearance
  shadows?: Shadow[]
  innerShadows?: Shadow[]
  blur?: Blur
}

/** Same as StyleProperties but allowing text or layer style override  */
export interface OverridableStyleProperties extends StyleProperties {
  isDirty: boolean
  /**
   * This contains the original style properties coming from the potential text or layer style.
   * Only the props that are overwritten will appear here with their original value.
   * We use this to get the style of the original layer or text style and show the out of sync tooltip.
   */
  originalSharedStyleValues?: Partial<StyleProperties>
}

export interface Border {
  isEnabled: boolean
  fillType: FillType
  color?: Color
  gradient?: Gradient
  position: BorderPosition
  thickness: number
  appearance: Appearance
}

export interface BorderOptions {
  dashPattern: number[] | null
  lineCapStyle: 'butt' | 'round' | 'square'
  lineJoinStyle: 'miter' | 'round' | 'bevel'
}

export enum BorderPosition {
  Center = 'center',
  Inside = 'inside',
  Outside = 'outside',
}

export interface Appearance {
  opacity: number
  blendMode: string
}

export interface Fill {
  isEnabled: boolean
  type: FillType
  color?: Color
  gradient?: Gradient
  patternFillType?: ImageFillType
  patternTileScale?: number
  appearance: Appearance
  /** DEPRECATED, this doesn't seem to be used */
  noiseIndex?: number
  /** DEPRECATED, this doesn't seem to be used */
  noiseIntensity?: number
}

export enum FillType {
  Color = 'color',
  Gradient = 'gradient',
  Pattern = 'pattern',
}

export enum ImageFillType {
  Fit = 'fit',
  Fill = 'fill',
  Stretch = 'stretch',
  Tile = 'tile',
}

export interface Shadow {
  isEnabled: boolean
  color: Color
  offsetX: number
  offsetY: number
  blurRadius: number
  spread?: number
  appearance?: Appearance
  blendMode?: string
}

export interface Blur {
  isEnabled: boolean
  type: 'motion' | 'zoom' | 'background' | 'gaussian'
  center?: Point
  motionAngle?: number
  radius?: number
  saturation?: number
}

export interface GradientStops {
  color: Color
  position: number
}

export enum GradientType {
  Linear = 'linear',
  Radial = 'radial',
  Angular = 'angular',
}

export interface Gradient {
  stops: GradientStops[]
  from: Point
  to: Point
  type: GradientType
}
